import {ZipArchive} from "../../types/types";
import * as React from "react";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import {ListItemIcon, ListItemText, MenuList, Popover, Typography, useTheme} from "@mui/material";
import {StyledButton} from "../Common/StyledButton";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FileDownloadIcon from "@mui/icons-material/FileDownload";


import {styled} from '@mui/material/styles';
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import MuiAccordion, {AccordionProps} from '@mui/material/Accordion';
import MuiAccordionSummary, {
    AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import {getAssetDownloadUrl} from "../../common/helpers";

type SubMenuType = 'document' | 'image' | 'video';

interface CombinedDownloadMenuProps {
    items: Record<SubMenuType, {
        [k: string]: ZipArchive,
    }>,
    locale: string
}

const Accordion = styled((props: AccordionProps) => (
    <MuiAccordion disableGutters elevation={0} square {...props}>{props.children}</MuiAccordion>
))(({theme}) => ({
    border: `1px solid ${theme.palette.divider}`,
    '&:not(:last-child)': {
        borderBottom: 0,
    },
    '&::before': {
        display: 'none',
    },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary
        expandIcon={<ArrowRightIcon/>}
        {...props}
    />
))(({theme}) => ({
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
        transform: 'rotate(90deg)',
    },
    '& .MuiAccordionSummary-content': {
        marginLeft: theme.spacing(1),
    },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({theme}) => ({
    borderTop: '1px solid rgba(0, 0, 0, .125)',
    padding: 0,
}));

const StyledPopover = styled(Popover)(({theme}) => ({
    '& .MuiPopover-paper': {
        minWidth: 200,
    },
    '& .MuiAccordion-root': {
        width: "100%",
    },
    '& .MuiAccordionSummary-root': {
        width: "100%",
    }
}));

const subMenuLabels: Record<SubMenuType, string> = {
    "document": "Documents",
    "image": "Images",
    "video": "Videos",
}

const CombinedDownloadMenu = ({items, locale}: CombinedDownloadMenuProps) => {

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [calculatedWidth, setCalculatedWidth] = React.useState(0);

    const isOpen = anchorEl !== null;

    const theme = useTheme();

    const [expanded, setExpanded] = React.useState<string | false>(false);

    const handleChange =
        (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
            setExpanded(newExpanded ? panel : false);
        };

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setCalculatedWidth(event.currentTarget && event.currentTarget.offsetWidth);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setExpanded(false);
    };

    if (!items || 
        Object.keys(items).reduce((acc, key) => acc + Object.keys(items[key as SubMenuType]).length, 0) === 0) {
        return null;
    }

    return <Box sx={{flexGrow: 0, ml: "auto"}}>
        <StyledButton
            disableRipple
            aria-controls={isOpen ? "fade-menu" : undefined}
            aria-describedby="combomenu-popover"
            aria-haspopup="true"
            aria-expanded={isOpen ? "true" : undefined}
            onClick={handleOpenMenu}
            variant="contained"
            endIcon={isOpen ? <ArrowDropUpIcon/> : <ArrowDropDownIcon/>}
        >
            <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
                Download all media
            </Box>
            <FileDownloadIcon sx={{ display: { xs: "inline", md: "none" } }}/>
        </StyledButton>
        <StyledPopover
            id="combomenu-popover"
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleCloseMenu}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            sx={{width: Math.max(calculatedWidth, 250), mt: theme.spacing(1)}}
        >
            {Object.keys(items).map(key => Object.keys(items[key as SubMenuType]).length !== 0 &&
                <Accordion expanded={expanded === `panel-${key}`} onChange={handleChange(`panel-${key}`)}
                           sx={{width: calculatedWidth}}>
                    <AccordionSummary aria-controls={`panel-${key}-content`} id={`panel-${key}-header`}
                                      sx={{width: calculatedWidth}}>
                        <Typography>{subMenuLabels[key as SubMenuType]}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <MenuList dense sx={{p: 0, pb: 0, pt: 0, pl: 0}}>
                            {Object.keys(items[key as SubMenuType]).map(renditionType => <MenuItem 
                                component="a"
                                href={getAssetDownloadUrl(items[key as SubMenuType][renditionType].path!)}
                            >
                                <ListItemIcon>
                                    <FileDownloadIcon/>
                                </ListItemIcon>
                                <ListItemText>
                                        {items[key as SubMenuType][renditionType].labels?.[locale] ||
                                            items[key as SubMenuType][renditionType].labels?.["en-US"]}
                                </ListItemText>
                            </MenuItem>)}
                        </MenuList>
                    </AccordionDetails>
                </Accordion>)}
        </StyledPopover>
    </Box>
}

export default CombinedDownloadMenu;